import Card from "@/components/Card";
import { calculateTotalGrade } from "@/lib/utils";
import { AssessmentResult as TAssessmentResult } from "@/schema/assessment-results/AssessmentResult";
import AssessmentDetails from "@/src/_components/AssessmentResult/AssessmentDetails";
import GradeCard from "@/src/_components/AssessmentResult/GradeCard";
import GradeTable from "@/src/_components/AssessmentResult/GradeTable";
import SDGIndicatorDetails from "@/src/_components/AssessmentResult/SDGIndicatorDetails";
import { SDGRatings } from "@/src/_components/AssessmentResult/SDGRatings";
import ScoreCard from "@/src/_components/AssessmentResult/ScoreCard";
import ScoreChart from "@/src/_components/AssessmentResult/ScoreChart";
import ScoreTable from "@/src/_components/AssessmentResult/ScoreTable";
import ResultHeading, { HeadingType } from "@/src/_components/ResultHeading";
import ResultRadar from "@/src/_components/ResultRadar";
import { useTranslation } from "react-i18next";

interface AssessmentResultProps {
  assessmentResult: TAssessmentResult;
}

function AssessmentResult({ assessmentResult }: AssessmentResultProps) {
  const { t } = useTranslation();

  const {
    company,
    result: { main_category_results, score, sdg_goal_results, sdg_score },
    updated_at,
  } = assessmentResult;

  const totalPossibleScore = main_category_results.length * 100;
  const grade = calculateTotalGrade(totalPossibleScore, score);
  const showSdg = sdg_score != null && sdg_goal_results.length > 0;

  return (
    <>
      <div
        className=" min-w-[1400px] mx-4 mt-8 sm:mx-16"
        id="assessment-result"
      >
        <div className="bg-white w-[1120px] p-[36px]">
          <ResultHeading
            type={HeadingType.EVALUATION_REPORT}
            companyCode={company.registration_number ?? "---"}
            updatedAt={updated_at}
          />
          <Card className="pdf-component flex flex-col gap-8 mt-5 py-5 px-6 ">
            <div className="flex flex-row justify-between w-full lg:basis-1/3">
              <h2 className="text-lg font-bold text-cool-gray-900">
                {t("assessment-automatic-scoring-results")}
              </h2>
            </div>

            <div className="flex flex-row gap-8 ">
              <div className="flex grow flex-col gap-6 basis-2/3">
                <div className="flex gap-6 flex-row">
                  <GradeCard className="basis-1/2" grade={grade} />
                  <ScoreCard className="basis-1/2" score={score} />
                </div>
                <GradeTable
                  grade={grade}
                  totalPossibleScore={totalPossibleScore}
                />
                <div className="h-[150px] w-full">
                  <ScoreChart total={totalPossibleScore} score={score} />
                </div>
                <div>
                  <ResultRadar
                    data={main_category_results.map(({ content, score }) => ({
                      label: content,
                      points: score,
                    }))}
                  />
                </div>
              </div>

              <div className="w-full basis-1/3">
                <ScoreTable
                  score={score}
                  categories={main_category_results.map((category) => ({
                    id: category.id,
                    content: category.content,
                    score: category.score,
                  }))}
                />
              </div>
            </div>
          </Card>
          {showSdg && (
            <div className="pdf-component mt-8">
              <SDGRatings score={sdg_score} sdgGoalResults={sdg_goal_results} />
            </div>
          )}
          {showSdg && (
            <div className="pdf-component mt-8 flex flex-col gap-y-6">
              <SDGIndicatorDetails sdgGoalResults={sdg_goal_results} />
            </div>
          )}
          <div className="mt-[32px] flex flex-col gap-y-6">
            <AssessmentDetails mainCategoryResults={main_category_results} />
          </div>
        </div>
      </div>
    </>
  );
}

export default AssessmentResult;
